import { useEffect, useRef, useState } from "react";
import { CircleHelp, ExternalLink, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApi } from "@/lib/api";

const FIND_AIRPORT_URL = "https://www.fltplan.com/AwMPtoFindNearbyAirport.exe?CRN10=1&CARRYUNAME=PILOT&MODE=FindNearby&end=end";

function AirportHelp({ testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Help finding an airport" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-[#C6A55C] transition-colors" data-testid={`${testid}-help-btn`}>
          <CircleHelp size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 bg-[#0d0f14] border-white/10 text-zinc-300 text-xs leading-relaxed" data-testid={`${testid}-help`}>
        <p className="text-white text-sm mb-1.5">Not sure which airport?</p>
        <p>Type a city, state or country here to see airports nearby. To search around an exact address or place, use the FltPlan nearby airport finder, then type the airport code you pick here.</p>
        <a href={FIND_AIRPORT_URL} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[#C6A55C] hover:text-white transition-colors font-semibold" data-testid={`${testid}-help-link`}>
          Find a nearby airport <ExternalLink size={12} />
        </a>
      </PopoverContent>
    </Popover>
  );
}

export default function AirportInput({ value, onChange, className, testid, ...rest }) {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const typed = useRef(false);

  useEffect(() => {
    if (!typed.current || (value || "").trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/airports/search", { params: { q: value } });
        setItems(data); setActive(0); setOpen(true);
      } catch { setItems([]); }
    }, 180);
    return () => clearTimeout(t);
  }, [value, api]);

  const pick = (a) => { typed.current = false; onChange(a.label); setOpen(false); setItems([]); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <Input {...rest} className={`${className || ""} pr-9`} value={value} autoComplete="off" data-testid={testid}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }}
        onKeyDown={onKey} onFocus={() => items.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      <AirportHelp testid={testid} />
      {open && items.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 md:min-w-[380px] mt-1 max-h-80 overflow-auto bg-[#0d0f14] border border-white/10 shadow-2xl" data-testid={`${testid}-suggestions`}>
          {items.map((a, i) => (
            <li key={`${a.code}-${i}`} onMouseDown={(e) => { e.preventDefault(); pick(a); }} onMouseEnter={() => setActive(i)}
              className={`flex gap-3 px-3 py-2.5 cursor-pointer transition-colors ${i === active ? "bg-[#1a2540]" : "hover:bg-white/5"}`}
              data-testid={`${testid}-option-${i}`}>
              <MapPin size={14} className="mt-0.5 shrink-0 text-[#5B7FD1]" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate"><span className="font-mono-data text-[#5B7FD1]">{a.code}</span>{a.iata && a.iata !== a.code ? <span className="font-mono-data text-zinc-400"> · {a.iata}</span> : null} — {a.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{a.where}{a.type === "heliport" ? " · Heliport" : a.type === "seaplane_base" ? " · Seaplane base" : ""}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
